import React, { useState } from 'react';
import { History, ChevronDown, ChevronUp, Trash2, RefreshCw, Copy } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент для отображения истории сгенерированных хешей
 */
const HashHistoryList = ({ 
  hashHistory, 
  applyHistoryItem, // Переименовано из useHistoryItem
  deleteHistoryItem, 
  clearHistory 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  // Если нет истории, не отображаем компонент
  if (!hashHistory || hashHistory.length === 0) return null;

  // Обработчик копирования хеша в буфер обмена
  const copyToClipboard = (hash, id) => {
    navigator.clipboard.writeText(hash)
      .then(() => {
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
      })
      .catch(err => {
        console.error('Ошибка при копировании хеша:', err);
        alert('Не удалось скопировать хеш в буфер обмена.');
      });
  };

  // Определяем, показывать ли подробную историю или только последние несколько записей
  const displayHistory = isExpanded ? hashHistory : hashHistory.slice(0, 3);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 mt-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <History className="w-5 h-5 mr-2 text-blue-500" />
          История хешей ({hashHistory.length})
        </h3>

        <div className="flex items-center space-x-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setIsExpanded(!isExpanded)}
            title={isExpanded ? "Свернуть историю" : "Показать все записи"}
          >
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={clearHistory}
            title="Очистить историю"
            icon={<Trash2 className="w-4 h-4 text-red-500" />}
          >
            Очистить
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {displayHistory.map((item) => (
          <div 
            key={item.id} 
            className="p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <div className="flex items-start justify-between">
              <div className="flex-grow mr-2">
                <div className="flex items-center mb-1">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                    {item.plaintext.substring(0, 24)}{item.plaintext.length > 24 ? '...' : ''}
                  </span>
                  {item.isGenerated && (
                    <span className="ml-2 px-1.5 py-0.5 text-xs bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 rounded">
                      Автоматически
                    </span>
                  )}
                </div>
                <div className="font-mono text-xs text-gray-600 dark:text-gray-400 truncate mb-2">
                  {item.hash}
                </div>
                <div className="flex items-center text-xs text-gray-500 dark:text-gray-500">
                  <span>{new Date(item.date).toLocaleString('ru-RU')}</span>
                  <span className="mx-2">•</span>
                  <span>{item.rounds} раундов</span>
                </div>
              </div>
              
              <div className="flex items-center space-x-1">
                <button
                  type="button"
                  onClick={() => copyToClipboard(item.hash, item.id)}
                  className={`p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors ${
                    copiedId === item.id ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'
                  }`}
                  title={copiedId === item.id ? "Скопировано!" : "Копировать хеш"}
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => applyHistoryItem(item.id)}
                  className="p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 text-blue-600 dark:text-blue-400 transition-colors"
                  title="Использовать этот хеш"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => deleteHistoryItem(item.id)}
                  className="p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 text-red-600 dark:text-red-400 transition-colors"
                  title="Удалить из истории"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Показать/скрыть кнопку при наличии более 3 элементов */}
      {hashHistory.length > 3 && !isExpanded && (
        <button
          onClick={() => setIsExpanded(true)}
          className="w-full mt-3 py-2 flex items-center justify-center text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800 transition-colors"
        >
          <ChevronDown className="w-4 h-4 mr-1" />
          Показать все ({hashHistory.length})
        </button>
      )}
    </div>
  );
};

export default HashHistoryList;